import React from 'react';

import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Checkbox,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from '@mui/material';

import { ExtranetPendingChange } from '~/services/ExtranetService';

import ChangeRow from './ChangeRow';
import { HEADER_CELL_SX, PAGE_SIZE, PARTNER_PROPERTY_COLUMN_WIDTH } from './constants';
import type { ChangeMetadata } from './types';

interface ExtranetApprovalsTableProps {
  rows: Array<ExtranetPendingChange>;
  total: number;
  page: number;
  isLoading: boolean;
  isPendingTab: boolean;
  isHistoryTab: boolean;
  isMutating: boolean;
  metadataByChangeId: Map<string, ChangeMetadata>;
  selected: Set<string>;
  allSelected: boolean;
  onToggleSelect: (id: string) => void;
  onToggleSelectAll: () => void;
  onViewDiff: (row: ExtranetPendingChange) => void;
  onRowAction: (type: 'approve' | 'reject', id: string) => void;
  onPageChange: (page: number) => void;
}

export default function ExtranetApprovalsTable({
  rows,
  total,
  page,
  isLoading,
  isPendingTab,
  isHistoryTab,
  isMutating,
  metadataByChangeId,
  selected,
  allSelected,
  onToggleSelect,
  onToggleSelectAll,
  onViewDiff,
  onRowAction,
  onPageChange,
}: ExtranetApprovalsTableProps) {
  const colCount = isPendingTab ? 7 : 6;

  return (
    <>
      <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              {isPendingTab && (
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selected.size > 0 && !allSelected}
                    onChange={onToggleSelectAll}
                    inputProps={{ 'aria-label': 'Select all pending changes' }}
                    icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                    checkedIcon={<CheckBoxIcon fontSize="small" />}
                  />
                </TableCell>
              )}
              {isPendingTab && <TableCell sx={{ ...HEADER_CELL_SX, width: 160 }}>Action</TableCell>}
              <TableCell sx={{ ...HEADER_CELL_SX, width: 120 }}>Submitted</TableCell>
              <TableCell
                sx={{
                  ...HEADER_CELL_SX,
                  width: PARTNER_PROPERTY_COLUMN_WIDTH,
                  maxWidth: PARTNER_PROPERTY_COLUMN_WIDTH,
                }}
              >
                Partner / Property
              </TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, width: '18%' }}>Changed by</TableCell>
              <TableCell sx={{ ...HEADER_CELL_SX, width: 70 }}>Tab</TableCell>
              <TableCell sx={HEADER_CELL_SX}>Change (old → new)</TableCell>
              {isHistoryTab && <TableCell sx={{ ...HEADER_CELL_SX, width: 160 }}>Status</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No records found
                </TableCell>
              </TableRow>
            )}
            {rows.map((row) => (
              <ChangeRow
                key={row.id}
                row={row}
                metadata={metadataByChangeId.get(row.id)}
                showActions={isPendingTab}
                showStatus={isHistoryTab}
                selected={selected.has(row.id)}
                isMutating={isMutating}
                onToggleSelect={() => onToggleSelect(row.id)}
                onViewDiff={() => onViewDiff(row)}
                onAction={(type) => onRowAction(type, row.id)}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={total}
        page={page - 1}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
        onPageChange={(_, nextPageIndex) => onPageChange(nextPageIndex + 1)}
      />
    </>
  );
}
